import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from '../api/passwordReset';
import { validatePassword, validateConfirmPassword } from '../utils/validation';

// The confirm-reset / new-password form (bl-777). This is the landing route for
// the emailed reset link — identity-service builds `{FRONTEND_URL}/reset-password
// ?token=<token>` (src/email.rs::password_reset_link), so this MUST live on the
// front-door origin. Reads `token` from the query, sets a new password via
// `/reset-password`.
//
// MIRRORS LoginPage/RegisterPage conventions: reuses `register__*` classes,
// <Link> nav, data-testid on every interactive element, and the SAME shared
// validatePassword parity rules registration uses for pre-submit feedback (the
// server re-validates + is authoritative).

type Status =
  | 'idle'
  | 'submitting'
  | 'reset'
  | 'tokenFailed'
  | 'error';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  // A server weak-password rejection (400 with a strength reason) — distinct from
  // the client-side pre-submit fieldError. Surfaced inline; the form is retained.
  const [serverPasswordError, setServerPasswordError] = useState<string | null>(
    null,
  );
  const [status, setStatus] = useState<Status>('idle');

  // ── Missing-token guard ─────────────────────────────────────────────────────
  // No token in the URL → nothing to reset. Show an invalid-link state, no form.
  if (!token) {
    return (
      <main className="register" data-testid="reset-password-page">
        <section className="register__card">
          <h1 className="register__title">Invalid reset link</h1>
          <p
            className="register__subtitle"
            data-testid="reset-password-missing-token"
          >
            This reset link looks incomplete. Request a new one to continue.
          </p>
          <p className="register__footer">
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new-link"
            >
              Request a new reset link
            </Link>
          </p>
        </section>
      </main>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerPasswordError(null);

    // Pre-submit feedback with the SAME parity rules as registration (the server
    // re-validates + is authoritative). First failing reason wins.
    const pwErr = validatePassword(password);
    if (pwErr) {
      setFieldError(pwErr);
      return;
    }
    const confirmErr = validateConfirmPassword(password, confirm);
    if (confirmErr) {
      setFieldError(confirmErr);
      return;
    }
    setFieldError(null);

    setStatus('submitting');
    const result = await confirmPasswordReset(token, password);

    switch (result.kind) {
      case 'reset':
        setStatus('reset');
        return;
      case 'weakPassword':
        // Correctable: keep the form, surface the server's strength reason.
        setServerPasswordError(result.reason);
        setStatus('idle');
        return;
      case 'tokenFailed':
        setStatus('tokenFailed');
        return;
      default:
        setStatus('error');
        return;
    }
  }

  // ── Success state ───────────────────────────────────────────────────────────
  if (status === 'reset') {
    return (
      <main className="register" data-testid="reset-password-page">
        <section className="register__card">
          <h1 className="register__title">Password updated</h1>
          <p
            className="register__subtitle"
            data-testid="reset-password-success"
          >
            Your password&apos;s updated. Sign in with your new one.
          </p>
          <p className="register__footer">
            <Link to="/login" data-testid="reset-password-sign-in-link">
              Sign in
            </Link>
          </p>
        </section>
      </main>
    );
  }

  // ── Terminal token-failure state ────────────────────────────────────────────
  // Uniform for unknown / expired / consumed / mismatch — request a fresh link.
  if (status === 'tokenFailed') {
    return (
      <main className="register" data-testid="reset-password-page">
        <section className="register__card">
          <h1 className="register__title">Link expired or invalid</h1>
          <p
            className="register__subtitle"
            data-testid="reset-password-token-failed"
          >
            This reset link has expired or has already been used. Request a new
            one.
          </p>
          <p className="register__footer">
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new-link"
            >
              Request a new reset link
            </Link>
          </p>
        </section>
      </main>
    );
  }

  // ── New-password form ─────────────────────────────────────────────────────
  return (
    <main className="register" data-testid="reset-password-page">
      <section className="register__card">
        <h1 className="register__title">Choose a new password</h1>
        <p className="register__subtitle">
          Enter a new password for your Hello World DAO account.
        </p>

        {status === 'error' && (
          <p
            className="register__banner register__banner--block"
            data-testid="reset-password-error"
          >
            Something went wrong on our end. Give it another try.
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate data-testid="reset-password-form">
          <label className="register__field">
            <span>New password</span>
            <input
              type="password"
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-new"
            />
          </label>

          <label className="register__field">
            <span>Confirm new password</span>
            <input
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm"
            />
          </label>

          {fieldError && (
            <p
              className="register__error"
              data-testid="reset-password-field-error"
            >
              {fieldError}
            </p>
          )}
          {serverPasswordError && (
            <p
              className="register__error"
              data-testid="reset-password-server-error"
            >
              {serverPasswordError}
            </p>
          )}

          <button
            type="submit"
            className="register__submit"
            disabled={status === 'submitting'}
            data-testid="reset-password-submit"
          >
            {status === 'submitting' ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <p className="register__footer">
          <Link to="/login" data-testid="reset-password-back-to-login">
            Back to sign in
          </Link>
        </p>
      </section>
    </main>
  );
}
